import { ChatComposeBoxAddAttachmentMenuItem } from '@glade-ai/chat-plugin/ui';
import React, { type ComponentPropsWithoutRef, type FC, forwardRef, useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import SearchInput from '@/components/FormFields/SearchInput';
import Calendar from '@/components/Icons/Calendar';
import ProgressIndicator from '@/components/ProgressIndicator';
import useNoodleApi from '@/hooks/useNoodleApi';
import TeamsContext from '@/providers/Teams/TeamsContext';
import * as tsClient from '@/tsClient';
import type * as ApiModels from '@/typings/api-models';

import s from '../InputComposer.module.scss';

type Props = {
  onAttach: (data: { priceId: string; title: string }) => void;
};

const ADD_BOOKING_LABEL = 'Bookings';
const ADD_BOOKING_ICON = 'ph--calendar--regular';

export const AddBookingMenuItem = forwardRef<
  HTMLDivElement,
  Omit<ComponentPropsWithoutRef<typeof ChatComposeBoxAddAttachmentMenuItem>, 'icon' | 'label'>
>((props, forwardedRef) => (
  <ChatComposeBoxAddAttachmentMenuItem
    {...props}
    ref={forwardedRef}
    icon={ADD_BOOKING_ICON}
    label={ADD_BOOKING_LABEL}
  />
));
AddBookingMenuItem.displayName = 'AddBookingMenuItem';

export const AddBookingPopoverContent: FC<Props> = ({ onAttach }) => {
  const { creatorId } = useContext(TeamsContext);
  const [search, setSearch] = useState('');
  const {
    data: schedulingPrices,
    getData: getSchedulingPrices,
    fetchingState: { isFetching: isFetchingSchedulingPrices },
  } = useNoodleApi(tsClient.prices.getSchedulingPrices);

  useEffect(() => {
    if (creatorId) {
      getSchedulingPrices({ creatorId, page: 1, perPage: 1000 });
    }
  }, [creatorId, getSchedulingPrices]);

  const handleClick = (price: Pick<ApiModels.Price, 'id' | 'priceTitle'>): void => {
    onAttach({ priceId: price.id, title: price.priceTitle || 'Booking' });
  };

  if (schedulingPrices?.items.length === 0) {
    return null;
  }

  return (
    <>
      {!schedulingPrices || isFetchingSchedulingPrices ? (
        <ProgressIndicator isCentered />
      ) : (
        <>
          <SearchInput
            label='Filter by title'
            placeholder='Type a title...'
            setSearch={setSearch}
            className={s.search}
          />
          {schedulingPrices.items
            .filter((price) => price.priceTitle?.toLowerCase().includes(search.toLowerCase()))
            .map((price) => (
              <Buttons
                className={s.documentRequestButton}
                key={price.id}
                isWrapper
                isFlat
                onClick={() => handleClick(price)}
              >
                <Calendar size={14} weight='fill' color='var(--color-primary)' />
                <p>{price.priceTitle}</p>
              </Buttons>
            ))}
        </>
      )}
    </>
  );
};
